import { Icon } from '@iconify/react'
import { Draggable, Droppable } from 'react-beautiful-dnd'

import {
    getColumnBandClass,
    getGapSplitStyle,
    getJourneySpan,
    stepLaneEndId,
    stepLaneId,
} from '../utils'
import InsertZone from './InsertZone'
import StepCard from './StepCard'
import styles from '../../ProjectStoryMapBoard.module.css'

const AddStepCard = ({ track, boardTracks, trackIndex, onInsertStepAt }) => (
    <div
        className={`${styles.stepColumnCell} ${getColumnBandClass(
            boardTracks,
            trackIndex,
            styles
        )}`}
    >
        <button
            type="button"
            className={styles.addStepCard}
            onClick={() => onInsertStepAt(track.journeyId, 0)}
        >
            <Icon icon="mdi:plus" width={14} />
            ADD STEP
        </button>
    </div>
)

const StepGapCell = ({ gapSplitStyle, style }) => (
    <div
        className={`${styles.columnGap} ${styles.columnGapSplit}`}
        style={{ ...style, ...gapSplitStyle }}
        aria-hidden="true"
    />
)

const StepInsertOverlay = ({
    insertIndex,
    boardTracks,
    trackIndex,
    onInsertStepAt,
    journeyId,
}) => {
    const gapSplitStyle = getGapSplitStyle(boardTracks, trackIndex, styles)

    return (
        <InsertZone
            variant="step"
            label="Add step"
            className={styles.stepInsertOverlay}
            style={{
                left: `calc(var(--sb-step-width, 200px) * ${insertIndex + 1} + var(--sb-insert-width, 28px) * ${insertIndex} + var(--sb-insert-width, 28px) / 2)`,
            }}
            gapSplitStyle={gapSplitStyle}
            dataAfter={insertIndex}
            onClick={() => onInsertStepAt(journeyId, insertIndex + 1)}
        />
    )
}

const JourneyStepLane = ({
    journey,
    boardTracks,
    gridColumn,
    span,
    isLastJourney,
    onInsertStepAt,
    onOpenStep,
    editingStepId,
    editingStepDraft,
    stepInputRef,
    onStepDraftChange,
    onCommitStepTitle,
    draggingStepJourneyId,
    stepDragPlaceholder,
    stepMenu,
    highlightedCardId,
    isFilterActive = false,
    filterFocusedCardId = null,
    readOnly = false,
}) => {
    const emptyTrackIndex = boardTracks.findIndex(
        (track) => track.type === 'emptyStep' && track.journeyId === journey.id
    )

    const isDraggingLane = String(draggingStepJourneyId) === String(journey.id)
    const isCrossJourneyDrag =
        draggingStepJourneyId &&
        stepDragPlaceholder?.journeyId &&
        String(draggingStepJourneyId) !== String(stepDragPlaceholder.journeyId)
    const isCrossSourceLane = isDraggingLane && isCrossJourneyDrag
    const isDropTargetLane =
        String(stepDragPlaceholder?.journeyId) === String(journey.id) &&
        !isDraggingLane

    const stepInsertTracks = boardTracks
        .map((track, trackIndex) => ({ track, trackIndex }))
        .filter(
            ({ track }) =>
                track.type === 'gap' &&
                track.gapKind === 'step' &&
                track.journeyId === journey.id
        )

    if (journey.steps.length === 0) {
        return (
            <div
                key={`step-lane-${journey.id}`}
                className={`${styles.journeyStepLane} ${
                    isLastJourney ? styles.journeyStepLaneLast : ''
                }`}
                style={{ gridColumn: `${gridColumn} / span ${span}` }}
            >
                <Droppable
                    droppableId={stepLaneId(journey.id)}
                    type="STEP"
                    direction="horizontal"
                >
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`${styles.stepDroppableLane} ${styles.stepDroppableLaneEmpty} ${
                                snapshot.isDraggingOver ? styles.stepDroppableOver : ''
                            }`}
                            data-story-map-step-lane={String(journey.id)}
                        >
                            {!readOnly && !isFilterActive ? (
                                <AddStepCard
                                    track={{ journeyId: journey.id }}
                                    boardTracks={boardTracks}
                                    trackIndex={
                                        emptyTrackIndex >= 0 ? emptyTrackIndex : 0
                                    }
                                    onInsertStepAt={onInsertStepAt}
                                />
                            ) : null}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        )
    }

    return (
        <div
            key={`step-lane-${journey.id}`}
            className={`${styles.journeyStepLane} ${
                isDraggingLane ? styles.journeyStepLaneDragging : ''
            } ${isCrossSourceLane ? styles.journeyStepLaneCrossSource : ''} ${
                isDropTargetLane ? styles.journeyStepLaneDropTarget : ''
            } ${isLastJourney ? styles.journeyStepLaneLast : ''}`}
            style={{ gridColumn: `${gridColumn} / span ${span}` }}
        >
            <Droppable
                droppableId={stepLaneId(journey.id)}
                type="STEP"
                direction="horizontal"
            >
                {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`${styles.stepDroppableLane} ${
                                snapshot.isDraggingOver ? styles.stepDroppableOver : ''
                            }`}
                            data-story-map-step-lane={String(journey.id)}
                        >
                        {journey.steps.map((step) => {
                            const trackIndex = boardTracks.findIndex(
                                (track) =>
                                    track.type === 'step' && track.step.id === step.id
                            )
                            const stepIndex = journey.steps.findIndex(
                                (item) => item.id === step.id
                            )

                            const isEditingStep =
                                String(editingStepId) === String(step.id)

                            return (
                                <Draggable
                                    key={step.id}
                                    draggableId={`step-${step.id}`}
                                    index={stepIndex}
                                    isDragDisabled={
                                        readOnly ||
                                        isEditingStep ||
                                        (isFilterActive && step.matchesFilter === false)
                                    }
                                >
                                    {(dragProvided, dragSnapshot) => (
                                        <StepCard
                                            step={step}
                                            boardTracks={boardTracks}
                                            trackIndex={trackIndex}
                                            dragProvided={dragProvided}
                                            isDragging={dragSnapshot.isDragging}
                                            isEditing={isEditingStep}
                                            editDraft={
                                                isEditingStep
                                                    ? editingStepDraft
                                                    : ''
                                            }
                                            titleInputRef={
                                                isEditingStep
                                                    ? stepInputRef
                                                    : null
                                            }
                                            onDraftChange={onStepDraftChange}
                                            onCommitTitle={onCommitStepTitle}
                                            onOpen={onOpenStep}
                                            stepMenu={stepMenu}
                                            isHighlighted={
                                                String(highlightedCardId) ===
                                                String(step.id)
                                            }
                                            isFilterActive={isFilterActive}
                                            isFilterMatchCurrent={
                                                String(filterFocusedCardId) ===
                                                String(step.id)
                                            }
                                        />
                                    )}
                                </Draggable>
                            )
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>

            {readOnly || isFilterActive
                ? null
                : stepInsertTracks.map(({ track, trackIndex }) => (
                      <StepInsertOverlay
                          key={`step-overlay-${track.journeyId}-${track.insertStepIndex}`}
                          insertIndex={track.insertStepIndex - 1}
                          boardTracks={boardTracks}
                          trackIndex={trackIndex}
                          onInsertStepAt={onInsertStepAt}
                          journeyId={journey.id}
                      />
                  ))}
        </div>
    )
}

const JourneyStepGapDrop = ({
    leftJourney,
    gapSplitStyle,
    onInsertStepAt,
    style,
    readOnly = false,
    isFilterActive = false,
}) => (
    <div className={styles.journeyStepGapDrop} style={style}>
        {!readOnly ? (
            <>
                <Droppable
                    droppableId={stepLaneEndId(leftJourney.id)}
                    type="STEP"
                    direction="horizontal"
                >
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`${styles.journeyStepGapDropTarget} ${
                                snapshot.isDraggingOver
                                    ? styles.journeyStepGapDropOver
                                    : ''
                            }`}
                            style={gapSplitStyle}
                            data-story-map-step-lane-end={String(leftJourney.id)}
                        >
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
                {!isFilterActive ? (
                    <InsertZone
                        variant="step"
                        label="Add step"
                        className={styles.journeyStepGapInsert}
                        gapSplitStyle={gapSplitStyle}
                        onClick={() =>
                            onInsertStepAt(leftJourney.id, leftJourney.steps.length)
                        }
                    />
                ) : null}
            </>
        ) : (
            <StepGapCell gapSplitStyle={gapSplitStyle} />
        )}
    </div>
)

const StepRow = ({
    boardTracks,
    journeys,
    onInsertStepAt,
    onOpenStep,
    editingStepId,
    editingStepDraft,
    stepInputRef,
    onStepDraftChange,
    onCommitStepTitle,
    draggingStepJourneyId,
    stepDragPlaceholder,
    stepMenu,
    highlightedCardId,
    isFilterActive = false,
    filterFocusedCardId = null,
    readOnly = false,
}) => {
    let gridColumn = 1
    const elements = []

    journeys.forEach((journey, journeyIndex) => {
        const span = getJourneySpan(journey.steps.length)

        elements.push(
            <JourneyStepLane
                key={journey.id}
                journey={journey}
                boardTracks={boardTracks}
                gridColumn={gridColumn}
                span={span}
                isLastJourney={journeyIndex === journeys.length - 1}
                onInsertStepAt={onInsertStepAt}
                onOpenStep={onOpenStep}
                editingStepId={editingStepId}
                editingStepDraft={editingStepDraft}
                stepInputRef={stepInputRef}
                onStepDraftChange={onStepDraftChange}
                onCommitStepTitle={onCommitStepTitle}
                draggingStepJourneyId={draggingStepJourneyId}
                stepDragPlaceholder={stepDragPlaceholder}
                stepMenu={stepMenu}
                highlightedCardId={highlightedCardId}
                isFilterActive={isFilterActive}
                filterFocusedCardId={filterFocusedCardId}
                readOnly={readOnly}
            />
        )

        gridColumn += span

        if (journeyIndex < journeys.length - 1) {
            const journeyGapTrackIndex = boardTracks.findIndex(
                (track) =>
                    track.type === 'gap' &&
                    track.gapKind === 'journey' &&
                    track.journeyIndex === journeyIndex
            )
            const gapSplitStyle = getGapSplitStyle(
                boardTracks,
                journeyGapTrackIndex,
                styles
            )
            const gapJourney = journeys.find((j) => j.id === journey.id)

            if (gapJourney && gapJourney.steps.length > 0) {
                elements.push(
                    <JourneyStepGapDrop
                        key={`step-journey-gap-drop-${journeyIndex}`}
                        leftJourney={gapJourney}
                        gapSplitStyle={gapSplitStyle}
                        style={{ gridColumn }}
                        onInsertStepAt={onInsertStepAt}
                        readOnly={readOnly}
                        isFilterActive={isFilterActive}
                    />
                )
            } else {
                elements.push(
                    <StepGapCell
                        key={`step-journey-gap-${journeyIndex}`}
                        gapSplitStyle={gapSplitStyle}
                        style={{ gridColumn }}
                    />
                )
            }
            gridColumn += 1
        }
    })

    if (journeys.length > 0 && !readOnly && !isFilterActive) {
        const trailingTrackIndex = boardTracks.findIndex(
            (track) => track.type === 'gap' && track.gapKind === 'trailing'
        )
        const lastJourney = journeys[journeys.length - 1]
        const gapSplitStyle = getGapSplitStyle(boardTracks, trailingTrackIndex, styles)

        elements.push(
            <InsertZone
                key="step-insert-trailing"
                variant="step"
                label="Add step"
                gapSplitStyle={gapSplitStyle}
                style={{ gridColumn }}
                onClick={() =>
                    onInsertStepAt(lastJourney.id, lastJourney.steps.length)
                }
            />
        )
    }

    return elements
}

export default StepRow
